import React from 'react';
import { trackEvent } from '../hooks/usePageTracking';

const SKILL_OPTIONS = [
  { value: 'IT', label: 'IT / Technik' },
  { value: 'Recht', label: 'Recht / Jura' },
  { value: 'Campaigning', label: 'Campaigning / Wahlkampf' },
  { value: 'Spenden', label: 'Geldspende' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Design', label: 'Design / Grafik' },
  { value: 'Anderes', label: 'Anderes...' },
];

interface FormErrors {
  name?: string;
  birth_year?: string;
  email?: string;
}

const validate = (name: string, birth_year: string, email: string): FormErrors => {
  const errors: FormErrors = {};
  if (!name.trim()) {
    errors.name = 'Name ist erforderlich.';
  } else if (name.trim().length < 2) {
    errors.name = 'Name muss mindestens 2 Zeichen lang sein.';
  }
  if (!birth_year) {
    errors.birth_year = 'Geburtsjahr ist erforderlich.';
  } else {
    const year = parseInt(birth_year, 10);
    if (isNaN(year) || year < 1900 || year > 2014) {
      errors.birth_year = 'Bitte gib ein gültiges Geburtsjahr ein (1900–2014).';
    }
  }
  if (!email.trim()) {
    errors.email = 'E-Mail-Adresse ist erforderlich.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
  }
  return errors;
};

const Join: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [serverError, setServerError] = React.useState('');
  const [selectedSkills, setSelectedSkills] = React.useState<string[]>([]);
  const [otherSkill, setOtherSkill] = React.useState('');
  const [formErrors, setFormErrors] = React.useState<FormErrors>({});
  const formStarted = React.useRef(false);

  const handleFirstInput = () => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackEvent('form_start', 'mitmachen');
    }
  };

  const handleSkillChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const chosen = Array.from(e.target.selectedOptions).map((o) => o.value);
    setSelectedSkills(chosen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const name = (fd.get('name') as string) || '';
    const birth_year = (fd.get('birth_year') as string) || '';
    const email = (fd.get('email') as string) || '';

    const errors = validate(name, birth_year, email);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setIsSubmitting(true);
    try {
      const data: Record<string, any> = Object.fromEntries(fd.entries());
      data.skills = selectedSkills.map((s) => (s === 'Anderes' && otherSkill ? otherSkill : s));
      const response = await fetch('/api/signup/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        trackEvent('form_submit', 'mitmachen');
        setSuccessMessage('Danke – wir melden uns bald bei dir!');
        form.reset();
        setSelectedSkills([]);
        setOtherSkill('');
        formStarted.current = false;
      } else {
        setServerError('Fehler beim Absenden. Bitte versuche es erneut.');
      }
    } catch {
      setServerError('Netzwerkfehler. Bitte prüfe deine Verbindung.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="main-content page-enter">
      <section className="participation-section">
        <h2 className="tag-line">Lust dabei zu sein?</h2>
        {successMessage && <div className="success-message">{successMessage}</div>}
        {serverError && <div className="error-message">{serverError}</div>}
        <form onSubmit={handleSubmit} className="participation-form" noValidate>

          <div className="form-section">
            <h3>Persönliche Angaben</h3>
            <input
              type="text"
              name="name"
              onInput={handleFirstInput}
              placeholder="Name"
              className={formErrors.name ? 'form-input-error' : ''}
              aria-describedby={formErrors.name ? 'error-name' : undefined}
            />
            {formErrors.name && <p className="form-error" id="error-name">{formErrors.name}</p>}
            <input
              type="number"
              name="birth_year"
              placeholder="Geburtsjahr"
              className={formErrors.birth_year ? 'form-input-error' : ''}
              aria-describedby={formErrors.birth_year ? 'error-birth-year' : undefined}
              style={{ marginTop: '0.5rem' }}
            />
            {formErrors.birth_year && <p className="form-error" id="error-birth-year">{formErrors.birth_year}</p>}
          </div>

          <div className="form-section">
            <h3>Kontakt</h3>
            <input
              type="email"
              name="email"
              placeholder="E-Mail-Adresse"
              className={formErrors.email ? 'form-input-error' : ''}
              aria-describedby={formErrors.email ? 'error-email' : undefined}
            />
            {formErrors.email && <p className="form-error" id="error-email">{formErrors.email}</p>}
            <input
              type="tel"
              name="phone"
              placeholder="Handynummer (freiwillig)"
              style={{ marginTop: '0.5rem' }}
            />
          </div>

          <div className="form-section">
            <h3>Fähigkeiten <span className="optional-label">(freiwillig)</span></h3>
            <select
              multiple
              value={selectedSkills}
              onChange={handleSkillChange}
              className="skills-select"
              size={SKILL_OPTIONS.length}
            >
              {SKILL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="skills-hint">Mehrere Einträge mit Ctrl / Cmd + Klick auswählen</p>
            {selectedSkills.includes('Anderes') && (
              <input
                type="text"
                placeholder="Was kannst du einbringen?"
                value={otherSkill}
                onChange={(e) => setOtherSkill(e.target.value)}
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </div>

          <div className="form-section">
            <h3>Social Media <span className="optional-label">(freiwillig)</span></h3>
            <input type="text" name="instagram_handle" placeholder="Instagram-Handle" />
            <input type="url" name="linkedin_url" placeholder="LinkedIn-URL" style={{ marginTop: '0.5rem' }} />
          </div>

          <div className="form-section">
            <h3>Nachricht <span className="optional-label">(freiwillig)</span></h3>
            <textarea
              name="personal_message"
              placeholder="Deine persönliche Nachricht"
              rows={4}
            />
          </div>

          <button type="submit" disabled={isSubmitting} className="submit-button">
            {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
          </button>
        </form>
      </section>
    </main>
  );
};

export default Join;
